import * as THREE from "three"
import {GLTFLoader} from "../vendor/three/examples/jsm/loaders/GLTFLoader.js"
import {AR_FOV_H} from "./ar_model.js"
import {
  aglForZone,
  createStickyZone,
  facingForZone,
  pinFlag,
  sizeMForZone,
  sizeModeForZone,
  ZONE_COLOR,
} from "./ar_zones.js"
import {SIM_CAM_PLACE, SIM_PIN, emitSimCam, enToSim, simToEn} from "./sim_cam_geo.js"
import {
  TERRAIN_HALF_M,
  TERRAIN_SEGS,
  applyHeightToPlane,
  loadHeightField,
} from "./sim_terrain.js"
import {parseRedactPolygonsJson, simCamRedacted} from "./ar_redact.js"
import {dollySignFromTarget, dollyStepM, holdCodeFromTarget} from "./ar_sim_hud.js"

const EYE_H = 1.55
const TURN_RAD_S = 1.5
const KEYS = new Set(["KeyW", "KeyA", "KeyS", "KeyD", "KeyQ", "KeyE", "KeyR", "KeyF"])

export const ARSim = {
  mounted() {
    const canvas = this.el.querySelector("canvas")
    const distEl = this.el.querySelector("[data-ar-sim-dist]")
    if (!canvas) return

    const renderer = new THREE.WebGLRenderer({canvas, antialias: true, alpha: false})
    renderer.setClearColor(0x87a0b8, 1)
    renderer.outputColorSpace = THREE.SRGBColorSpace

    const scene = new THREE.Scene()
    scene.fog = new THREE.Fog(0x87a0b8, 2500, 7000)
    scene.add(new THREE.HemisphereLight(0xe8f0ff, 0x3d4a3a, 1.05))
    const sun = new THREE.DirectionalLight(0xfff4d6, 1.1)
    sun.position.set(40, 70, 20)
    scene.add(sun)

    const camera = new THREE.PerspectiveCamera(45, 1, 0.15, 8000)
    camera.rotation.order = "YXZ"

    const overlayGroup = new THREE.Group()
    scene.add(overlayGroup)
    const scaleRefs = buildScaleRefs()
    scene.add(scaleRefs)
    const ground = scaleRefs.getObjectByName("sim-ground")

    const heightPole = buildHeightPole(12)
    scene.add(heightPole)

    const shells = {
      exclusion: zoneShell(ZONE_COLOR.exclusion),
      activation: zoneShell(ZONE_COLOR.activation),
      detection: zoneShell(ZONE_COLOR.detection),
      beacon: zoneShell(ZONE_COLOR.beacon),
      remote: zoneShell(ZONE_COLOR.remote),
    }
    for (const shell of Object.values(shells)) scene.add(shell)

    const signs = {
      exclusion: groundSign("Exclusion", ZONE_COLOR.exclusion),
      activation: groundSign("Activation", ZONE_COLOR.activation),
      detection: groundSign("Detection", ZONE_COLOR.detection),
      beacon: groundSign("Beacon", ZONE_COLOR.beacon),
      remote: groundSign("Remote", ZONE_COLOR.remote),
    }
    for (const sign of Object.values(signs)) scene.add(sign)

    const loader = new GLTFLoader()
    const texLoader = new THREE.TextureLoader()
    let overlay = null
    let videoEl = null
    const sticky = createStickyZone()
    const keys = new Set()
    const look = {yaw: 0, pitch: -0.06}
    const pos = new THREE.Vector3(0, EYE_H, 40)
    let eyeAbove = EYE_H
    let heightField = null
    let terrainGen = 0
    let terrainKey = ""
    let dragging = false
    let lastX = 0
    let lastY = 0
    let dollyDir = 0
    let running = true
    let last = performance.now()

    const params = () => readParams(this.el.dataset)
    const redactPolygons = () => parseRedactPolygonsJson(this.el.dataset.redactPolygons)

    const groundY = () => (heightField ? heightField.relAtSim(pos.x, pos.z) : 0)
    const stickEye = () => {
      pos.y = groundY() + eyeAbove
    }

    const refreshTerrain = () => {
      const lat = Number(this.el.dataset.lat)
      const lng = Number(this.el.dataset.lng)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
      const key = `${lat.toFixed(4)},${lng.toFixed(4)}`
      if (key === terrainKey) return
      terrainKey = key
      const gen = ++terrainGen
      loadHeightField(lat, lng)
        .then((field) => {
          if (gen !== terrainGen || !running) return
          heightField = field
          const geo = new THREE.PlaneGeometry(
            TERRAIN_HALF_M * 2,
            TERRAIN_HALF_M * 2,
            TERRAIN_SEGS,
            TERRAIN_SEGS,
          )
          geo.rotateX(-Math.PI / 2)
          applyHeightToPlane(geo, field)
          ground.geometry.dispose()
          ground.rotation.set(0, 0, 0)
          ground.geometry = geo
          stickEye()
        })
        .catch(() => {
          if (terrainKey === key) terrainKey = ""
        })
    }

    const disposeVideo = () => {
      if (!videoEl) return
      videoEl.pause()
      videoEl.removeAttribute("src")
      videoEl.load()
      videoEl = null
    }

    const applyOverlay = () => {
      const p = params()
      setZoneShell(shells.exclusion, p.exclusion, true)
      setZoneShell(shells.activation, p.activation, true)
      setZoneShell(shells.detection, p.detection, true)
      setZoneShell(shells.beacon, p.beacon, true)
      setZoneShell(shells.remote, p.remote, p.remoteFinite && p.remote > p.beacon)
      placeRingSign(signs.exclusion, p.exclusion, true)
      placeRingSign(signs.activation, p.activation, true)
      placeRingSign(signs.detection, p.detection, true)
      placeRingSign(signs.beacon, p.beacon, true)
      placeRingSign(signs.remote, p.remote, p.remoteFinite && p.remote > p.beacon)
      sticky.reset()
      overlayGroup.position.set(0, p.aglActivation, 0)
      overlayGroup.rotation.set(0, 0, 0)
      heightPole.position.set(Math.max(p.sizeActivation, 1.2) * 0.8 + 3.2, 0, 6)
      setHeightPole(heightPole, Math.max(p.aglDetection + p.sizeDetection * 0.6, 4))
      fitSimView(camera, scene, ground, 0, p)
      loadOverlay(p, loader, texLoader, overlayGroup, disposeVideo, (obj, vid) => {
        overlay = obj
        videoEl = vid || null
        if (videoEl) playSimVideo(videoEl)
      })
    }

    const resize = () => {
      const w = Math.max(1, canvas.clientWidth)
      const h = Math.max(1, canvas.clientHeight)
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
      renderer.setSize(w, h, false)
      camera.aspect = w / h
      camera.fov = (2 * Math.atan(Math.tan(AR_FOV_H / 2) / camera.aspect) * 180) / Math.PI
      camera.updateProjectionMatrix()
    }

    const tick = (now) => {
      if (!running) return
      const dt = Math.min(0.05, (now - last) / 1000)
      last = now
      stepMove(pos, look, keys, dt)
      if (dollyDir) {
        const dir = new THREE.Vector3(0, 0, -1).applyEuler(
          new THREE.Euler(look.pitch, look.yaw, 0, "YXZ"),
        )
        pos.addScaledVector(dir, dollyDir * dollyStepM(Math.hypot(pos.x, pos.z), dt))
        eyeAbove = Math.max(0.4, Math.min(80, pos.y - groundY()))
      }
      if (keys.has("KeyQ")) look.yaw += TURN_RAD_S * dt
      if (keys.has("KeyE")) look.yaw -= TURN_RAD_S * dt
      if (keys.has("EyeUp") || keys.has("KeyR")) eyeAbove += 4 * dt
      if (keys.has("EyeDown") || keys.has("KeyF")) eyeAbove -= 4 * dt
      eyeAbove = Math.max(0.4, Math.min(80, eyeAbove))
      stickEye()
      camera.position.copy(pos)
      camera.rotation.set(look.pitch, look.yaw, 0)
      const p = params()
      const dist = Math.hypot(pos.x, pos.z)
      fitSimView(camera, scene, ground, dist, p)
      const zone = sticky.update(dist, 4, radiiOf(p), now)
      const redacted = simCamRedacted(p.lat, p.lng, pos.x, pos.z, redactPolygons())
      if (overlay) {
        poseSimOverlay(overlay, overlayGroup, camera, p, canvas, dist, zone)
        overlayGroup.visible = overlayGroup.visible && !redacted
      }
      faceBillboards(scene, camera)
      scaleHeightPole(heightPole, camera)
      if (distEl) {
        const hide = redacted ? " · hidden" : ""
        distEl.textContent = `${dist.toFixed(1)} m · ${zone}${hide} · eye ${pos.y.toFixed(2)} m · 65° HFOV`
      }
      publishCam()
      renderer.render(scene, camera)
      requestAnimationFrame(tick)
    }

    let lookId = null
    const endLook = (e) => {
      if (lookId == null) return
      if (e && e.pointerId !== lookId) return
      dragging = false
      lookId = null
    }
    const onLookMove = (e) => {
      if (!dragging || e.pointerId !== lookId) return
      look.yaw -= (e.clientX - lastX) * 0.005
      look.pitch -= (e.clientY - lastY) * 0.004
      look.pitch = Math.max(-1.2, Math.min(0.9, look.pitch))
      lastX = e.clientX
      lastY = e.clientY
    }
    canvas.addEventListener(
      "pointerdown",
      (e) => {
        if (e.button) return
        if (lookId != null) return
        e.preventDefault()
        dragging = true
        lookId = e.pointerId
        lastX = e.clientX
        lastY = e.clientY
        try {
          canvas.setPointerCapture(e.pointerId)
        } catch {
          /* iOS may reject capture; window pointermove/up still drive the drag. */
        }
      },
      {passive: false},
    )
    window.addEventListener("pointermove", onLookMove, {capture: true, passive: false})
    window.addEventListener("pointerup", endLook, true)
    window.addEventListener("pointercancel", endLook, true)

    const hud = this.el.querySelector("[data-ar-sim-hud]")
    const hudHolds = new Map()
    const applyDollyFromHolds = () => {
      dollyDir = 0
      for (const held of hudHolds.values()) {
        if (held.dolly) dollyDir = held.dolly
      }
    }
    const onHudDown = (e) => {
      if (e.button) return
      const code = holdCodeFromTarget(e.target)
      const dolly = dollySignFromTarget(e.target)
      if (!code && !dolly) return
      e.preventDefault()
      hudHolds.set(e.pointerId, {code, dolly})
      if (code) keys.add(code)
      if (dolly) dollyDir = dolly
    }
    const onHudRelease = (e) => {
      const held = hudHolds.get(e.pointerId)
      if (!held) return
      hudHolds.delete(e.pointerId)
      if (held.code) keys.delete(held.code)
      applyDollyFromHolds()
    }
    const onHudMenu = (e) => e.preventDefault()
    const panicGestures = () => {
      dragging = false
      lookId = null
      dollyDir = 0
      keys.clear()
      hudHolds.clear()
    }
    const onVis = () => {
      if (document.visibilityState === "hidden") panicGestures()
    }
    window.addEventListener("pointerup", onHudRelease, true)
    window.addEventListener("pointercancel", onHudRelease, true)
    document.addEventListener("visibilitychange", onVis)
    if (hud) {
      hud.addEventListener("pointerdown", onHudDown, {passive: false})
      hud.addEventListener("contextmenu", onHudMenu)
    }
    canvas.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault()
        const dir = new THREE.Vector3(0, 0, -1).applyEuler(new THREE.Euler(look.pitch, look.yaw, 0, "YXZ"))
        const step = Math.max(2.2, Math.min(18, Math.hypot(pos.x, pos.z) * 0.04))
        pos.addScaledVector(dir, e.deltaY > 0 ? -step : step)
        eyeAbove = Math.max(0.4, Math.min(80, pos.y - groundY()))
        stickEye()
      },
      {passive: false},
    )

    const onKey = (e) => {
      if (!KEYS.has(e.code)) return
      if (e.type === "keydown") keys.add(e.code)
      else keys.delete(e.code)
    }
    window.addEventListener("keydown", onKey)
    window.addEventListener("keyup", onKey)

    const reset = () => {
      const p = params()
      const r = startRadius(p)
      pos.set(0, 0, r)
      eyeAbove = EYE_H
      stickEye()
      look.yaw = 0
      look.pitch = -Math.atan2(p.aglDetection - EYE_H, r)
      if (videoEl) {
        try {
          videoEl.currentTime = 0
        } catch {
          /* ignore */
        }
        playSimVideo(videoEl)
      }
    }

    this.el.querySelector("[data-ar-sim-reset]")?.addEventListener("click", (e) => {
      e.preventDefault()
      reset()
    })

    this.handleEvent("ar_sim", (payload) => {
      Object.assign(this.el.dataset, toDataset(payload))
      applyOverlay()
      refreshTerrain()
    })

    let lastPub = 0
    const publishCam = () => {
      const now = performance.now()
      if (now - lastPub < 50) return
      lastPub = now
      const en = simToEn(pos.x, pos.z)
      emitSimCam({east: en.east, north: en.north, yaw: look.yaw, pitch: look.pitch})
    }

    const onPlace = (event) => {
      const east = Number(event.detail?.east)
      const north = Number(event.detail?.north)
      if (!Number.isFinite(east) || !Number.isFinite(north)) return
      const xz = enToSim(east, north)
      pos.x = xz.x
      pos.z = xz.z
      stickEye()
    }
    window.addEventListener(SIM_CAM_PLACE, onPlace)

    const onPin = (event) => {
      const lat = Number(event.detail?.lat)
      const lng = Number(event.detail?.lng)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
      this.el.dataset.lat = String(lat)
      this.el.dataset.lng = String(lng)
      refreshTerrain()
    }
    window.addEventListener(SIM_PIN, onPin)

    const ro = new ResizeObserver(resize)
    ro.observe(canvas)
    this._simCleanup = () => {
      running = false
      terrainGen += 1
      dollyDir = 0
      keys.clear()
      ro.disconnect()
      window.removeEventListener("keydown", onKey)
      window.removeEventListener("keyup", onKey)
      window.removeEventListener("pointermove", onLookMove, {capture: true})
      window.removeEventListener("pointerup", endLook, true)
      window.removeEventListener("pointercancel", endLook, true)
      window.removeEventListener("pointerup", onHudRelease, true)
      window.removeEventListener("pointercancel", onHudRelease, true)
      document.removeEventListener("visibilitychange", onVis)
      dragging = false
      lookId = null
      window.removeEventListener(SIM_CAM_PLACE, onPlace)
      window.removeEventListener(SIM_PIN, onPin)
      if (hud) {
        hud.removeEventListener("pointerdown", onHudDown)
        hud.removeEventListener("contextmenu", onHudMenu)
      }
      hudHolds.clear()
      disposeVideo()
      renderer.dispose()
    }

    applyOverlay()
    reset()
    refreshTerrain()
    resize()
    requestAnimationFrame(tick)
  },

  destroyed() {
    this._simCleanup?.()
  },
}

function startRadius(p) {
  const act = Math.max(Number(p.activation) || 50, 1)
  const det = Math.max(Number(p.detection) || act + 1, act)
  return (act + det) / 2
}

function radiiOf(p) {
  return {
    exclusion: p.exclusion,
    activation: p.activation,
    detection: p.detection,
    beacon: p.beacon,
    remote: p.remote,
    remoteFinite: p.remoteFinite,
  }
}

function readParams(ds) {
  return {
    sizeActivation: Math.max(0.2, Number(ds.sizeActivation) || 1.5),
    sizeDetection: Math.max(0.2, Number(ds.sizeDetection) || 8),
    aglActivation: Number(ds.aglActivation) || 0,
    aglDetection: Number(ds.aglDetection) || 0,
    aglRemote: Number(ds.aglRemote) || 0,
    yaw: Number(ds.yaw) || 0,
    facing: ds.facing || "billboard",
    modelUrl: ds.modelUrl || "",
    mosaicUrl: ds.mosaicUrl || "",
    kind: ds.kind || "",
    exclusion: Number(ds.exclusion) || 8,
    activation: Number(ds.activation) || 50,
    detection: Number(ds.detection) || 200,
    beacon: Number(ds.beacon) || 2000,
    remote: Number(ds.remote) || 0,
    remoteFinite: pinFlag(ds.remoteFinite),
    beaconFraction: Number(ds.beaconFraction) || 0.36,
    remoteFraction: Number(ds.remoteFraction) || 0.36,
    lat: Number(ds.lat),
    lng: Number(ds.lng),
  }
}

function toDataset(payload) {
  return {
    sizeActivation: payload.size_activation,
    sizeDetection: payload.size_detection,
    aglActivation: payload.agl_activation,
    aglDetection: payload.agl_detection,
    aglRemote: payload.agl_remote,
    yaw: payload.yaw,
    facing: payload.facing,
    modelUrl: payload.model_url || "",
    mosaicUrl: payload.mosaic_url || "",
    kind: payload.kind || "",
    exclusion: payload.exclusion,
    activation: payload.activation,
    detection: payload.detection,
    beacon: payload.beacon,
    remote: payload.remote,
    remoteFinite: payload.remote_finite,
    beaconFraction: payload.beacon_fraction,
    remoteFraction: payload.remote_fraction,
    lat: payload.lat,
    lng: payload.lng,
  }
}

function poseSimOverlay(overlay, group, camera, p, canvas, dist, zone) {
  const pin = {
    altitude_agl_activation: p.aglActivation,
    altitude_agl_detection: p.aglDetection,
    altitude_agl_remote: p.aglRemote,
    apparent_size_activation: p.sizeActivation,
    apparent_size_detection: p.sizeDetection,
    beacon_viewport_fraction: p.beaconFraction,
    remote_viewport_fraction: p.remoteFraction,
  }
  const agl = aglForZone(zone, pin)
  group.position.set(0, agl, 0)
  group.visible = zone !== "exclusion" && zone !== "beyond"
  const slant = Math.hypot(dist, agl - camera.position.y)
  const sizeM = sizeMForZone(zone, pin, {
    canvasW: canvas.clientWidth,
    canvasH: canvas.clientHeight,
    fovH: AR_FOV_H,
    slantM: slant,
    aspect: overlay.userData.planeAspect || 1,
  })
  const mode = sizeModeForZone(zone, p.kind)
  const unit =
    mode === "longest"
      ? overlay.userData.longestEdge || overlay.userData.unitMetres || 1
      : overlay.userData.diameter || overlay.userData.unitMetres || 1
  overlay.scale.setScalar(sizeM / unit)
  applyFacing(overlay, group, camera, {
    ...p,
    facing: facingForZone(zone, p.facing),
  })
}

function applyFacing(overlay, group, camera, p) {
  overlay.up.set(0, 1, 0)
  overlay.quaternion.identity()
  const y = group.position.y
  if (p.facing === "world") {
    const yaw = (THREE.MathUtils.degToRad(p.yaw) || 0)
    overlay.lookAt(Math.sin(yaw), y, -Math.cos(yaw))
  } else {
    overlay.lookAt(camera.position.x, y, camera.position.z)
  }
}

const _billboardWorld = new THREE.Vector3()

function labelScale(camera, dist, geomH, targetNdc = 0.016, maxScale = 24) {
  const worldH =
    2 * Math.max(dist, 1) * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2) * targetNdc
  return THREE.MathUtils.clamp(worldH / Math.max(geomH, 0.05), 1, maxScale)
}

function faceBillboards(scene, camera) {
  scene.traverse((obj) => {
    if (obj.userData?.yawBillboard) {
      obj.getWorldPosition(_billboardWorld)
      obj.lookAt(camera.position.x, _billboardWorld.y, camera.position.z)
      if (obj.userData.tick) return
      const dist = camera.position.distanceTo(_billboardWorld)
      obj.scale.setScalar(labelScale(camera, dist, obj.userData.geomH || 0.38, 0.015, 14))
    }
    if (obj.userData?.ringSign) {
      const dist = camera.position.distanceTo(obj.position)
      obj.scale.setScalar(labelScale(camera, dist, obj.userData.geomH || 0.85, 0.03, 36))
    }
  })
}

function outerFiniteRadius(p) {
  const bea = Math.max(Number(p.beacon) || 0, Number(p.detection) || 0, 80)
  if (p.remoteFinite && Number(p.remote) > bea) return Number(p.remote)
  return bea
}

function fitSimView(camera, scene, ground, dist, p) {
  const outer = outerFiniteRadius(p)
  camera.far = Math.max(5000, outer * 3, dist * 3 + 1000)
  camera.near = 0.15
  camera.updateProjectionMatrix()
  if (scene.fog) {
    scene.fog.near = Math.max(outer * 1.15, 900)
    scene.fog.far = Math.max(camera.far * 0.92, outer * 2.5)
  }
  void ground
}

function stepMove(pos, look, keys, dt) {
  const reach = Math.hypot(pos.x, pos.z)
  const gait = reach > 80 ? Math.max(1, reach / 40) : 1
  const speed = (keys.has("ShiftLeft") || keys.has("ShiftRight") ? 16 : 7) * dt * gait
  const forward = new THREE.Vector3(Math.sin(look.yaw), 0, Math.cos(look.yaw))
  const right = new THREE.Vector3(Math.cos(look.yaw), 0, -Math.sin(look.yaw))
  if (keys.has("KeyW")) pos.addScaledVector(forward, -speed)
  if (keys.has("KeyS")) pos.addScaledVector(forward, speed)
  if (keys.has("KeyA")) pos.addScaledVector(right, -speed)
  if (keys.has("KeyD")) pos.addScaledVector(right, speed)
}

function zoneWallHeight(radius) {
  return Math.min(140, Math.max(36, radius * 0.07))
}

function zoneRingMaterial(color, opacity) {
  return new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    side: THREE.DoubleSide,
    depthWrite: false,
    fog: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
  })
}

function zoneShell(color) {
  const g = new THREE.Group()
  const ring = new THREE.Mesh(new THREE.RingGeometry(0.985, 1, 160), zoneRingMaterial(color, 0.92))
  ring.rotation.x = -Math.PI / 2
  ring.position.y = 0.08
  ring.name = "ring"
  ring.renderOrder = 3
  g.add(ring)

  const topRing = new THREE.Mesh(new THREE.RingGeometry(0.985, 1, 160), zoneRingMaterial(color, 0.78))
  topRing.rotation.x = -Math.PI / 2
  topRing.name = "topRing"
  topRing.renderOrder = 3
  g.add(topRing)

  const wall = new THREE.Mesh(
    new THREE.CylinderGeometry(1, 1, 1, 96, 1, true),
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.32,
      side: THREE.DoubleSide,
      depthWrite: false,
      fog: false,
    }),
  )
  wall.name = "wall"
  wall.renderOrder = 2
  g.add(wall)
  return g
}

function setZoneShell(g, radius, visible) {
  const r = Math.max(Number(radius) || 0, 0)
  g.visible = Boolean(visible && r > 0)
  if (!g.visible) return
  const h = zoneWallHeight(r)
  g.scale.set(r, 1, r)
  const wall = g.getObjectByName("wall")
  if (wall) {
    wall.scale.set(1, h, 1)
    wall.position.y = h / 2
  }
  const thick = Math.max(2.8, r * 0.012)
  const inner = Math.max(0.82, 1 - thick / r)
  for (const name of ["ring", "topRing"]) {
    const ring = g.getObjectByName(name)
    if (!ring) continue
    ring.geometry.dispose()
    ring.geometry = new THREE.RingGeometry(inner, 1, 160)
    if (name === "topRing") ring.position.y = h
  }
}

function placeRingSign(sign, radius, visible) {
  const r = Math.max(Number(radius) || 0, 1)
  sign.visible = Boolean(visible && r > 0)
  if (!sign.visible) return
  const out = Math.max(1.6, Math.min(10, r * 0.006))
  sign.position.set(Math.max(4, Math.min(48, r * 0.02)), 0.05, r + out)
}

function groundSign(title, color) {
  const g = new THREE.Group()
  const plate = new THREE.Mesh(
    new THREE.PlaneGeometry(2.6, 0.85),
    new THREE.MeshBasicMaterial({
      map: textTexture(`${title}`, color),
      transparent: true,
      side: THREE.DoubleSide,
      depthWrite: false,
      fog: false,
    }),
  )
  plate.rotation.x = -Math.PI / 2
  plate.position.y = 0.03
  g.add(plate)
  g.userData.ringSign = true
  g.userData.geomH = 0.85
  return g
}

function groundCheckTexture() {
  const canvas = document.createElement("canvas")
  canvas.width = 2
  canvas.height = 2
  const ctx = canvas.getContext("2d")
  ctx.fillStyle = "#647456"
  ctx.fillRect(0, 0, 2, 2)
  ctx.fillStyle = "#7a8b68"
  ctx.fillRect(0, 0, 1, 1)
  ctx.fillRect(1, 1, 1, 1)
  const tex = new THREE.CanvasTexture(canvas)
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.magFilter = THREE.NearestFilter
  tex.minFilter = THREE.NearestFilter
  tex.colorSpace = THREE.SRGBColorSpace
  const cells = (TERRAIN_HALF_M * 2) / 40
  tex.repeat.set(cells, cells)
  return tex
}

function buildScaleRefs() {
  const g = new THREE.Group()
  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(TERRAIN_HALF_M * 2, TERRAIN_HALF_M * 2, TERRAIN_SEGS, TERRAIN_SEGS),
    new THREE.MeshLambertMaterial({
      map: groundCheckTexture(),
      color: 0xffffff,
      flatShading: true,
    }),
  )
  ground.name = "sim-ground"
  ground.rotation.x = -Math.PI / 2
  g.add(ground)

  const road = new THREE.Mesh(
    new THREE.PlaneGeometry(8, 180),
    new THREE.MeshLambertMaterial({color: 0x4b5563}),
  )
  road.rotation.x = -Math.PI / 2
  road.position.set(10, 0.02, 0)
  g.add(road)

  g.add(apartment(-22, -8, 16.5, "16.5 m"))
  g.add(apartment(32, -18, 22, "22 m"))
  g.add(apartment(-28, 26, 11, "11 m"))
  g.add(person(6, 10))
  g.add(person(7.2, 11.4))
  g.add(car(11, 18))
  g.add(tree(-8, 14))
  g.add(lamp(8.5, 6))
  g.add(doorway(-22, 4))
  return g
}

function heightTag(text, x, y, z) {
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(1.35, 0.38),
    new THREE.MeshBasicMaterial({
      map: textTexture(text, 0xf8fafc),
      transparent: true,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
      fog: false,
    }),
  )
  mesh.position.set(x, y, z)
  mesh.userData.yawBillboard = true
  mesh.userData.geomH = 0.38
  mesh.renderOrder = 4
  return mesh
}

function buildHeightPole(maxH) {
  const g = new THREE.Group()
  g.userData.maxH = maxH
  const pole = new THREE.Mesh(
    new THREE.BoxGeometry(0.06, 1, 0.06),
    new THREE.MeshLambertMaterial({color: 0xf8fafc}),
  )
  pole.name = "pole"
  pole.position.y = 0.5
  g.add(pole)
  return g
}

function setHeightPole(g, maxH) {
  const h = Math.max(4, Math.min(40, maxH))
  const pole = g.getObjectByName("pole")
  if (pole) {
    pole.userData.height = h
    pole.scale.set(1, h, 1)
    pole.position.y = h / 2
  }
  const stale = []
  g.traverse((o) => {
    if (o.userData.tick) stale.push(o)
  })
  stale.forEach((o) => g.remove(o))
  const top = Math.floor(h)
  for (let m = 0; m <= top; m += 1) {
    const tick = new THREE.Mesh(
      new THREE.BoxGeometry(m % 5 === 0 ? 0.55 : 0.28, 0.03, 0.03),
      new THREE.MeshLambertMaterial({color: m % 5 === 0 ? 0xfbbf24 : 0xe5e7eb, fog: false}),
    )
    tick.position.set(0.22, m, 0)
    tick.userData.tick = true
    tick.userData.metres = m
    tick.userData.baseX = 0.22
    g.add(tick)
    if (m % 5 === 0 || m === 1 || m === top) {
      const tag = heightTag(`${m} m`, 0.95, m, 0)
      tag.userData.tick = true
      tag.userData.metres = m
      g.add(tag)
    }
  }
}

function poleLabelVisible(m, top, labelH) {
  if (m === 0 || m === top) return true
  if (labelH < 1.6) return m === 1 || m % 5 === 0
  if (labelH < 3.6) return m % 5 === 0
  if (labelH < 6.2) return m % 10 === 0
  return m % 20 === 0
}

function scaleHeightPole(g, camera) {
  const pole = g.getObjectByName("pole")
  const h = pole?.userData.height || 12
  g.getWorldPosition(_billboardWorld)
  _billboardWorld.y += Math.min(h, 4)
  const dist = camera.position.distanceTo(_billboardWorld)
  const s = labelScale(camera, dist, 0.38, 0.022, 28)
  const labelH = 0.38 * s
  const top = Math.floor(h)
  if (pole) pole.scale.set(Math.max(1, s * 0.35), h, Math.max(1, s * 0.35))
  g.traverse((o) => {
    if (!o.userData.tick) return
    const m = o.userData.metres
    if (o.userData.yawBillboard) {
      o.scale.setScalar(s)
      o.visible = poleLabelVisible(m, top, labelH)
      return
    }
    o.scale.setScalar(Math.max(1, s * 0.5))
    if (o.userData.baseX != null) o.position.x = o.userData.baseX * Math.min(6, 0.7 + s * 0.06)
  })
}

function box(w, h, d, color, x, y, z) {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), new THREE.MeshLambertMaterial({color}))
  m.position.set(x, y, z)
  return m
}

function apartment(x, z, height, label) {
  const g = new THREE.Group()
  const floors = Math.max(3, Math.round(height / 3.1))
  const h = floors * 3.1 + 1.0
  g.add(box(12, h, 10, 0xc4b8a5, x, h / 2, z))
  for (let floor = 0; floor < floors; floor += 1) {
    const fy = 1.6 + floor * 3.1
    for (let i = -2; i <= 2; i += 1) {
      g.add(box(1.2, 1.5, 0.12, 0x93c5fd, x + i * 2.0, fy, z + 5.06))
    }
  }
  g.add(box(12.4, 0.32, 10.4, 0x8b7355, x, h + 0.1, z))
  g.add(heightTag(label, x, h + 1.1, z))
  return g
}

function person(x, z) {
  const g = new THREE.Group()
  g.add(box(0.45, 0.7, 0.28, 0x1e3a5f, x, 1.05, z))
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.14, 12, 10), new THREE.MeshLambertMaterial({color: 0xe8c4a8}))
  head.position.set(x, 1.55, z)
  g.add(head)
  g.add(heightTag("1.7 m", x, 1.85, z))
  return g
}

function car(x, z) {
  const g = new THREE.Group()
  g.add(box(4.2, 1.15, 1.75, 0x2563eb, x, 0.7, z))
  g.add(box(2.1, 0.7, 1.7, 0x93c5fd, x - 0.2, 1.45, z))
  g.add(heightTag("1.5 m", x, 1.75, z))
  return g
}

function tree(x, z) {
  const g = new THREE.Group()
  g.add(box(0.35, 2.2, 0.35, 0x6b4423, x, 1.1, z))
  const crown = new THREE.Mesh(new THREE.SphereGeometry(1.6, 10, 8), new THREE.MeshLambertMaterial({color: 0x3f7a3a}))
  crown.position.set(x, 3.1, z)
  g.add(crown)
  g.add(heightTag("4.7 m", x, 4.9, z))
  return g
}

function lamp(x, z) {
  const g = new THREE.Group()
  g.add(box(0.12, 4.2, 0.12, 0x374151, x, 2.1, z))
  g.add(box(0.7, 0.18, 0.35, 0xfde68a, x, 4.25, z))
  g.add(heightTag("4.2 m", x, 4.6, z))
  return g
}

function doorway(x, z) {
  const g = new THREE.Group()
  g.add(box(1.1, 2.1, 0.2, 0x1f2937, x + 7.1, 1.05, z))
  g.add(heightTag("2.1 m", x + 7.1, 2.4, z))
  return g
}

function textTexture(text, color) {
  const c = document.createElement("canvas")
  c.width = 512
  c.height = 128
  const ctx = c.getContext("2d")
  ctx.fillStyle = "rgba(15, 23, 42, 0.78)"
  ctx.fillRect(8, 8, 496, 112)
  ctx.fillStyle = typeof color === "number" ? `#${color.toString(16).padStart(6, "0")}` : "#f8fafc"
  ctx.font = "700 52px ui-sans-serif, system-ui, sans-serif"
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  ctx.fillText(text, 256, 68)
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

function playSimVideo(el) {
  el.muted = true
  el.playsInline = true
  el.loop = true
  const play = el.play()
  if (play && typeof play.catch === "function") play.catch(() => {})
}

function loadOverlay(p, loader, texLoader, group, disposeVideo, done) {
  while (group.children.length) group.remove(group.children[0])
  disposeVideo()
  const url = p.modelUrl || p.mosaicUrl
  const kind = p.kind

  const finishGlb = (obj, dim) => {
    obj.userData.diameter = Math.hypot(dim.x, dim.y, dim.z) || 1
    obj.userData.longestEdge = Math.max(dim.x, dim.y, dim.z, 1e-8)
    obj.userData.unitMetres = obj.userData.diameter
    group.add(obj)
    done(obj, null)
  }

  if (kind === "glb" && url) {
    loader.load(url, (gltf) => {
      const obj = gltf.scene
      obj.updateWorldMatrix(true, true)
      const box3 = new THREE.Box3().setFromObject(obj)
      const dim = new THREE.Vector3()
      box3.getSize(dim)
      finishGlb(obj, dim)
    })
    return
  }

  if (kind === "mp4" && (p.mosaicUrl || url)) {
    const src = p.modelUrl || p.mosaicUrl || url
    const el = document.createElement("video")
    el.src = src
    el.crossOrigin = "anonymous"
    el.muted = true
    el.playsInline = true
    el.loop = true
    el.preload = "auto"
    const onReady = () => {
      const tex = new THREE.VideoTexture(el)
      tex.colorSpace = THREE.SRGBColorSpace
      const vw = el.videoWidth || 16
      const vh = el.videoHeight || 9
      const aspect = vw / vh
      const w = aspect >= 1 ? 1 : aspect
      const h = aspect >= 1 ? 1 / aspect : 1
      const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(w, h),
        new THREE.MeshBasicMaterial({map: tex, side: THREE.DoubleSide, transparent: true}),
      )
      mesh.userData.unitMetres = 1
      mesh.userData.longestEdge = 1
      mesh.userData.diameter = 1
      mesh.userData.planeAspect = aspect
      group.add(mesh)
      done(mesh, el)
    }
    el.addEventListener("loadeddata", onReady, {once: true})
    el.load()
    playSimVideo(el)
    return
  }

  if ((kind === "texture" || !kind) && (p.mosaicUrl || url)) {
    const src = url || p.mosaicUrl
    texLoader.load(src, (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace
      const aspect = (tex.image?.width || 1) / (tex.image?.height || 1)
      const w = aspect >= 1 ? 1 : aspect
      const h = aspect >= 1 ? 1 / aspect : 1
      const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(w, h),
        new THREE.MeshBasicMaterial({map: tex, side: THREE.DoubleSide, transparent: true}),
      )
      mesh.userData.unitMetres = 1
      mesh.userData.longestEdge = 1
      mesh.userData.diameter = 1
      mesh.userData.planeAspect = aspect
      group.add(mesh)
      done(mesh, null)
    })
    return
  }

  const marker = new THREE.Mesh(
    new THREE.OctahedronGeometry(0.5),
    new THREE.MeshLambertMaterial({color: 0x38bdf8, transparent: true, opacity: 0.85}),
  )
  marker.userData.unitMetres = 1
  group.add(marker)
  done(marker, null)
}
